import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import { FaHome, FaBriefcase, FaEnvelope, FaUserCircle } from "react-icons/fa";
import { useMediaQuery } from "react-responsive";
import "./Header.css";

/* ---------- Small, reusable Avatar with fallback initials ---------- */
function Avatar({ user, size = 32, className = "" }) {
  const [imgError, setImgError] = useState(false);

  const displayName =
    user?.displayName ||
    user?.firstName ||
    (user?.email ? user.email.split("@")[0] : "") ||
    "";

  const initials =
    displayName
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join("") || "U";

  const hasPhoto = !!user?.photo && !imgError;

  if (hasPhoto) {
    return (
      <img
        src={user.photo}
        alt={displayName || "User"}
        className={`user-icon ${className}`}
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div
      className={`avatar-initials ${className}`}
      style={{ width: size, height: size, fontSize: Math.max(12, size * 0.42) }}
      aria-label={displayName || "User"}
    >
      {initials}
    </div>
  );
}
/* ------------------------------------------------------------------ */

const Header = () => {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language);
  const [user, setUser] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
// put near your other useStates
// put near your other useStates
const [scrolled, setScrolled] = useState(false);

useEffect(() => {
  const target = document.getElementById('top-sentinel');
  if (!target) return;

  const io = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      // When the very top is NOT visible, we’re scrolled
      setScrolled(!entry.isIntersecting);
    },
    {
      root: null,           // viewport
      threshold: 0,         // flip as soon as it leaves
      rootMargin: '0px 0px 0px 0px'
    }
  );

  io.observe(target);
  return () => io.disconnect();
}, []);
  const dropdownRef = useRef(null);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const navigate = useNavigate();
  const location = useLocation();

  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const mobileMenuRef = useRef(null);

  // Track auth state (works for Google AND manual email/password)
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (fbUser) => {
      if (fbUser) {
        const firstName = fbUser.displayName
          ? fbUser.displayName.split(" ")[0]
          : fbUser.email
          ? fbUser.email.split("@")[0]
          : "";
        setUser({
          uid: fbUser.uid,
          firstName,
          displayName: fbUser.displayName || "",
          email: fbUser.email || "",
          photo: fbUser.photoURL || null, // ← no broken placeholder
        });
      } else {
        setUser(null);
      }
    });
    return () => unsub();
  }, []);

  // Close dropdown on outside click/touch
  useEffect(() => {
    const handleOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, []);

  // Mobile menu overlay handling
  useEffect(() => {
    const onOutside = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setShowMobileMenu(false);
      }
    };
    const onEsc = (e) => e.key === "Escape" && setShowMobileMenu(false);

    if (showMobileMenu) {
      document.body.style.overflow = "hidden";
      document.addEventListener("mousedown", onOutside);
      document.addEventListener("touchstart", onOutside, { passive: true });
      document.addEventListener("keydown", onEsc);
    }
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [showMobileMenu]);

  // Go to login preserving where we came from
  const goToLogin = () => {
    setShowDropdown(false);
    const current =
      window.location.pathname + window.location.search + window.location.hash;
    navigate(`/login?redirect=${encodeURIComponent(current)}`);
  };

  // Logout
  const handleLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      setUser(null);
      setShowDropdown(false);
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Language toggle
  const toggleLanguage = () => {
    const newLang = language === "en" ? "sv" : "en";
    i18n.changeLanguage(newLang);
    setLanguage(newLang);
  };

  return (
    <>
      {/* Desktop Header */}
      {!isMobile && (
        <motion.header
          className="header"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div className="logo">
            <Link to="/">CRIGO Technologies</Link>
          </div>

          <nav>
            <ul>
              <li>
                <Link to="/">{t("header.home")}</Link>
              </li>
              <li>
                <Link to="/services">{t("header.services")}</Link>
              </li>
              <li>
                <Link to="/contact">{t("header.contact")}</Link>
              </li>
              <li>
                <Link to="/pricing">{t("header.pricing")}</Link>
              </li>

              {/* Profile */}
              <li className="user-profile">
                <div
                  className="profile-container"
                  onClick={() => setShowDropdown((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={showDropdown}
                >
                  {user ? (
                    <Avatar user={user} size={32} />
                  ) : (
                    <FaUserCircle className="default-icon" />
                  )}
                </div>

                {/* Dropdown (desktop) */}
                {showDropdown && (
                  <div className="dropdown-menu" ref={dropdownRef}>
                    {user ? (
                      <>
                        <span className="user-name">
                          {user.firstName || t("header.my_account")}
                        </span>
                        <Link
                          to="/myaccount"
                          onClick={() => setShowDropdown(false)}
                        >
                          {t("header.my_account")}
                        </Link>
                        <button className="logout-btn" onClick={handleLogout}>
                          {t("header.logout")}
                        </button>
                      </>
                    ) : (
                      <button onClick={goToLogin}>{t("header.sign_in")}</button>
                    )}
                  </div>
                )}
              </li>

              {/* Language (desktop) */}
              <li>
                <div className="language-switcher" onClick={toggleLanguage}>
                  <span
                    className={`toggle ${language === "en" ? "active" : ""}`}
                  >
                    {language === "sv" ? "🇬🇧" : "🇸🇪"}
                  </span>
                </div>
              </li>
            </ul>
          </nav>
        </motion.header>
      )}

      {/* Mobile Top Bar */}
      {isMobile && (
        <motion.header
          className={`mobile-header-bar ${
            scrolled ? "scrolled" : "transparent"
          }`}
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div className="mobile-header">
            <Link to="/" className="mobile-logo">
              CRIGO Technologies
            </Link>

            <div className="mobile-header-right">
              <div className="mobile-language" onClick={toggleLanguage}>
                {language === "sv" ? "🇬🇧" : "🇸🇪"}
              </div>

              <div
                className="hamburger-menu"
                aria-label="Menu"
                aria-expanded={showMobileMenu}
                aria-controls="mobile-menu"
                role="button"
                tabIndex={0}
                onClick={() => setShowMobileMenu((v) => !v)}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") &&
                  setShowMobileMenu((v) => !v)
                }
              >
                <div className={`bar ${showMobileMenu ? "open" : ""}`} />
                <div className={`bar ${showMobileMenu ? "open" : ""}`} />
                <div className={`bar ${showMobileMenu ? "open" : ""}`} />
              </div>
            </div>
          </div>
        </motion.header>
      )}

      {/* Mobile slide-in menu */}
      {isMobile && showMobileMenu && (
        <motion.div
          className="mobile-menu-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.nav
            id="mobile-menu"
            className="mobile-menu-panel"
            ref={mobileMenuRef}
            initial={{ x: "-110%" }}
            animate={{ x: 0 }}
            exit={{ x: "-110%" }}
            transition={{ type: "tween", duration: 0.4, ease: "easeInOut" }}
            aria-label="Mobile menu"
          >
            <button
              className="mobile-menu-close"
              onClick={() => setShowMobileMenu(false)}
              aria-label="Close"
            >
              ✕
            </button>

            <Link to="/pricing" onClick={() => setShowMobileMenu(false)}>
              {t("header.pricing")}
            </Link>
            <Link to="/blog" onClick={() => setShowMobileMenu(false)}>
              Blog
            </Link>
            <Link to="/privacy" onClick={() => setShowMobileMenu(false)}>
              Privacy
            </Link>
          </motion.nav>
        </motion.div>
      )}

      {/* Mobile Bottom Nav */}
      {isMobile && (
        <nav
          className="bottom-nav"
          role="navigation"
          aria-label="Mobile bottom"
        >
          <Link to="/" className="nav-item">
            <FaHome />
            <span>{t("header.home")}</span>
          </Link>

          <Link to="/services" className="nav-item">
            <FaBriefcase />
            <span>{t("header.services")}</span>
          </Link>

          <Link to="/contact" className="nav-item">
            <FaEnvelope />
            <span>{t("header.contact")}</span>
          </Link>

          {/* Account (mobile) */}
          <button
            type="button"
            className="nav-item account"
            onClick={user ? () => setShowDropdown((v) => !v) : goToLogin}
            aria-label={
              user ? user.firstName || "Account" : t("header.sign_in")
            }
            aria-haspopup="menu"
            aria-expanded={!!user && showDropdown}
          >
            {user ? (
              <Avatar user={user} size={26} />
            ) : (
              <FaUserCircle className="default-icon" />
            )}
            <span>{user ? user.firstName : t("header.sign_in")}</span>
          </button>
        </nav>
      )}

      {/* ✅ DROPDOWN RENDERED OUTSIDE THE NAV ON MOBILE */}
      {isMobile && showDropdown && user && (
        <div className="mobile-dropdown" ref={dropdownRef} role="menu">
          <Link to="/myaccount" onClick={() => setShowDropdown(false)}>
            {t("header.my_account")}
          </Link>
          <button onClick={handleLogout}>{t("header.logout")}</button>
        </div>
      )}
    </>
  );
};

export default Header;
